import { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getBattleById } from "../services/OpenAiService";
import { useCachedFetch } from "../hooks/useCachedFetch";
import { Battle } from "../models/Battle";
import { FEEDBACK_FORM_URL } from "../utils/constants";
import CharacterCard from "./CharacterCard";
import BattleImage from "./BattleImage";

export default function BattleResult() {
  const { state: locationState } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  // Only fetches when there's no router-passed state to use directly
  // (a direct/shared URL, or a page refresh).
  const { data: fetched, loading, error } = useCachedFetch(
    () => getBattleById(id),
    { key: id, enabled: !locationState && Boolean(id) }
  );

  const result = locationState || fetched;
  const battle = useMemo(() => Battle.fromJSON(result), [result]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  // eslint-disable-next-line no-unused-vars
  const handleShareX = useCallback(() => {
    if (!battle) return;
    const c1 = battle.character1?.name;
    const c2 = battle.character2?.name;
    const winner = battle.winner?.name;
    const prob = battle.winner?.probability;
    const text = `${c1} vs ${c2} — AI says ${winner} wins${prob ? ` with ${prob}%` : ""}!`;
    const url = encodeURIComponent(window.location.href);
    window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${url}`, "_blank", "noopener");
  }, [battle]);

  // Dynamic page title
  useEffect(() => {
    if (!battle) return;
    const c1 = battle.character1?.name;
    const c2 = battle.character2?.name;
    if (c1 && c2) document.title = `${c1} vs ${c2} | Who Would Win`;
    return () => { document.title = "Who Would Win — AI-Powered Battle Arena"; };
  }, [battle]);

  if (loading) {
    return (
      <div className="empty-result">
        <div className="spinner" />
        <p className="empty-result-sub">Loading battle...</p>
      </div>
    );
  }

  if (error || !result) {
    return (
      <div className="empty-result">
        <div className="empty-result-icon">VS</div>
        <h2 className="empty-result-title">{error ? "Battle Not Found" : "No Battle Found"}</h2>
        <p className="empty-result-sub">
          {error ? error.message : "Pick your fighters and let the AI decide who wins."}
        </p>
        <button className="fight-btn" style={{ maxWidth: 320 }} onClick={() => navigate("/")}>
          Start a Battle
        </button>
      </div>
    );
  }

  return (
    <section className="result-wrap">
      <div className="result-actions">
        <button className="reset-btn reset-btn-primary" onClick={() => navigate("/")}>
          New Battle
        </button>
        <button
          className="reset-btn rematch-btn"
          onClick={() => navigate("/", { state: { char1: battle.character1?.name, char2: battle.character2?.name } })}
        >
          Rematch
        </button>
      </div>

      <div className="winner-banner">
        {battle.isDraw ? (
          <span>It's a Draw!</span>
        ) : (
          <span>
            Winner: <strong>{battle.winner.name}</strong>
            {typeof battle?.winner?.probability === "number"
              ? ` (${battle.winner.probability}%)`
              : ""}
          </span>
        )}
      </div>

      <div className="share-bar">
        <button className="share-btn share-copy" onClick={handleCopy}>
          {copied ? "Copied!" : "Copy Link"}
        </button>
        <a
          className="share-btn share-feedback"
          href={FEEDBACK_FORM_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Feedback
        </a>
        {/* hidden during beta — re-enable for public launch
        <button className="share-btn share-x" onClick={handleShareX}>
          Share on X
        </button>
        */}
      </div>

      <div className="scene-row">
        <div className="scene-image-col">
          <BattleImage prompt={battle.scenePrompt} imageKey={battle.sceneImageKey} />
        </div>
        <div className="why-winner">
          <span className="why-winner-label">The Verdict</span>
          <p className="why-winner-text">{battle.whyWinner}</p>
        </div>
      </div>

      {battle.narration?.length > 0 && (
        <div className="battle-log">
          <span className="battle-log-title">Battle Log</span>
          <div className="battle-log-entries">
            {battle.narration.map((entry, i) => (
              <div
                key={entry.round}
                className="battle-log-entry"
                style={{ animationDelay: `${0.3 + i * 0.25}s` }}
              >
                <div className="battle-log-round">
                  <span className="battle-log-round-num">Round {entry.round}</span>
                  {entry.title && <span className="battle-log-round-title">{entry.title}</span>}
                </div>
                <p className="battle-log-text">{entry.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="result-container">
        <CharacterCard character={battle.character1} isWinner={battle.isWinner(battle.character1.name)} opponent={battle.character2} />
        <CharacterCard character={battle.character2} isWinner={battle.isWinner(battle.character2.name)} opponent={battle.character1} />
      </div>

    </section>
  );
}
